import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import {
  validatePasswordResetToken,
  resetPassword,
} from '../services/LoginService';
import { routes } from '../routes/routePaths';

import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Input from '../components/input/Input';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import Link from '../components/link/Link';
import { useClientLogo } from '../hooks/useClientLogo';
import {
  getPasswordPolicyChecks,
  isPasswordPolicyChecksValid,
  isPasswordPolicyValid,
} from '../utils/passwordPolicy';

type PageState = 'loading' | 'invalid-token' | 'form' | 'success';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { logoUrl } = useClientLogo();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setPageState('invalid-token');
      return;
    }

    validatePasswordResetToken(token).then((response) => {
      if (response.success) {
        setUserEmail(response.email || '');
        setPageState('form');
      } else {
        setPageState('invalid-token');
      }
    });
  }, [token]);

  const validate = (values: { password: string; confirmPassword: string }) => {
    const errors: any = {};

    if (!values.password) {
      errors.password = 'El campo contraseña es obligatorio';
    } else if (!isPasswordPolicyValid(values.password)) {
      errors.password = 'La contraseña no cumple los requisitos';
    }

    if (!values.confirmPassword) {
      errors.confirmPassword = 'Debés confirmar la contraseña';
    } else if (values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validateOnChange: true,
    validate,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setErrorMsg('');

      const response = await resetPassword(
        token!,
        values.password,
        values.confirmPassword,
      );

      setIsSubmitting(false);

      if (response.success) {
        setPageState('success');
      } else {
        const errorMessages: Record<string, string> = {
          PASSWORD_RESET_TOKEN_EXPIRED: 'El enlace ha expirado. Solicitá uno nuevo.',
          PASSWORD_RESET_TOKEN_INVALID: 'El enlace no es válido.',
          PASSWORD_REQUIRED: 'Completá ambos campos.',
          PASSWORD_MISMATCH: 'Las contraseñas no coinciden.',
          PASSWORD_POLICY_VIOLATION: 'La contraseña no cumple los requisitos de seguridad.',
        };

        const code = response.code || '';
        if (code === 'PASSWORD_RESET_TOKEN_EXPIRED' || code === 'PASSWORD_RESET_TOKEN_INVALID') {
          setPageState('invalid-token');
        } else {
          setErrorMsg(errorMessages[code] || response.message || 'Ocurrió un error al cambiar la contraseña.');
        }
      }
    },
  });

  const passwordChecks = getPasswordPolicyChecks(formik.values.password);

  const renderLoading = () => (
    <Card display='flex' justifyContent='center' margin={{ top: '6', bottom: '6' }}>
      <Spinner size='md' variant='primary' />
    </Card>
  );

  const renderInvalidToken = () => (
    <>
      <H
        withMargin={false}
        variant='danger'
        type='3'
        weight='bolder'
        modifier='strong'
        align='center'
      >
        Enlace inválido o vencido
      </H>
      <Card margin={{ top: '3' }}>
        <P type='1' align='center' variant='neutral'>
          El enlace para restablecer tu contraseña ya no es válido. Puede que
          haya expirado o ya haya sido utilizado.
        </P>
      </Card>
      <Card
        display='flex'
        justifyContent='center'
        margin={{ top: '6', bottom: '3' }}
      >
        <Button
          type='button'
          id='reset-password-button-new-request'
          size='xl'
          fullWidth
          onClick={() => navigate(routes.forgotPassword)}
        >
          Solicitar nuevo enlace
        </Button>
      </Card>
      <Card display='flex' justifyContent='center'>
        <Link
          size='sm'
          variant='primary'
          target='_self'
          onClick={() => navigate(routes.login)}
        >
          Volver al inicio de sesión
        </Link>
      </Card>
    </>
  );

  const allChecksPassed = isPasswordPolicyChecksValid(passwordChecks);

  const passwordsMatch =
    formik.values.password === formik.values.confirmPassword &&
    formik.values.confirmPassword !== '';

  const isFormValid = allChecksPassed && passwordsMatch && !isSubmitting;

  const renderCheckItem = (passed: boolean, hasInput: boolean, label: string) => {
    if (!hasInput) {
      return (
        <P type='2' variant='neutral'>
          • {label}
        </P>
      );
    }
    return (
      <P type='2' variant={passed ? 'success' : 'danger'}>
        {passed ? '✓' : '✕'} {label}
      </P>
    );
  };

  const renderForm = () => (
    <>
      <H
        withMargin={false}
        variant='primary'
        type='3'
        weight='bolder'
        modifier='strong'
        align='center'
      >
        Crear nueva contraseña
      </H>
      <Card margin={{ top: '2' }}>
        <P type='1' align='center' variant='neutral'>
          Elegí una nueva contraseña para tu cuenta.
        </P>
        {userEmail && (
          <P type='1' align='center' variant='neutral' weight='bold'>
            {userEmail}
          </P>
        )}
      </Card>
      <form onSubmit={formik.handleSubmit}>
        <Card margin={{ top: '3' }}>
          <Card margin={{ bottom: '2' }}>
            <Input
              id='reset-password-input-password'
              inputStatus={
                formik.values.password && !allChecksPassed ? 'error' : 'normal'
              }
              label='Nueva contraseña'
              placeholder='Ingresá tu nueva contraseña'
              size='md'
              type='password'
              showPasswordButton
              onChange={(e) =>
                formik.setFieldValue('password', e.target.value)
              }
              onBlur={formik.handleBlur}
            />
            <Card spacing={{ left: '3' }} margin={{ top: '1' }}>
              {renderCheckItem(passwordChecks.hasMinLength, !!formik.values.password, 'mínimo 8 caracteres')}
              {renderCheckItem(passwordChecks.hasUppercase, !!formik.values.password, 'al menos 1 mayúscula')}
              {renderCheckItem(passwordChecks.hasLowercase, !!formik.values.password, 'al menos 1 minúscula')}
              {renderCheckItem(passwordChecks.hasSpecialChar, !!formik.values.password, 'al menos 1 símbolo')}
            </Card>
          </Card>

          <Card margin={{ bottom: '3' }}>
            <Input
              id='reset-password-input-confirm'
              inputStatus={
                formik.values.confirmPassword && !passwordsMatch
                  ? 'error'
                  : 'normal'
              }
              label='Repetir contraseña'
              placeholder='Volvé a ingresar la contraseña'
              size='md'
              type='password'
              showPasswordButton
              onChange={(e) =>
                formik.setFieldValue('confirmPassword', e.target.value)
              }
            />
            {formik.values.confirmPassword && !passwordsMatch && (
              <P variant='danger' withMargin={false} type='2'>
                ⚠ Las contraseñas no coinciden.
              </P>
            )}
          </Card>

          {errorMsg && (
            <P variant='danger' type='1' weight='bold' align='center'>
              {errorMsg}
            </P>
          )}

          <Card
            display='flex'
            justifyContent='center'
            margin={{ top: '4', bottom: '3' }}
          >
            <Button
              type='submit'
              id='reset-password-button-submit'
              size='xl'
              fullWidth
              disabled={!isFormValid}
            >
              {isSubmitting ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Guardar contraseña'
              )}
            </Button>
          </Card>
        </Card>
      </form>
    </>
  );

  const renderSuccess = () => (
    <>
      <H
        withMargin={false}
        variant='primary'
        type='3'
        weight='bolder'
        modifier='strong'
        align='center'
      >
        Se actualizó la contraseña
      </H>
      <Card margin={{ top: '3' }}>
        <P type='1' align='center' variant='neutral'>
          Tu contraseña fue actualizada correctamente. Ya podés iniciar sesión
          con tu nueva contraseña.
        </P>
      </Card>
      <Card
        display='flex'
        justifyContent='center'
        margin={{ top: '6', bottom: '3' }}
      >
        <Button
          type='button'
          id='reset-password-button-login'
          size='xl'
          fullWidth
          onClick={() => navigate(routes.login)}
        >
          Iniciar sesión
        </Button>
      </Card>
    </>
  );

  return (
    <Card
      variant='primary'
      borderRadius={false}
      modifier='regular'
      display='flex'
      justifyContent='center'
      height='100vh'
      width='100%'
      alignItems='center'
    >
      <Card
        boxShadow='1'
        spacing={{ top: '6', right: '4', bottom: '4', left: '4' }}
        width='360px'
        variant='neutral'
        height='auto'
        margin='auto'
        modifier='inverted'
      >
        <Card
          display='flex'
          justifyContent='center'
          margin={{ top: '6', bottom: '3' }}
        >
          <img
            src={logoUrl || '/assets/logos/logo.svg'}
            width='64px'
            alt='Logo'
          />
        </Card>

        {pageState === 'loading' && renderLoading()}
        {pageState === 'invalid-token' && renderInvalidToken()}
        {pageState === 'form' && renderForm()}
        {pageState === 'success' && renderSuccess()}
      </Card>
    </Card>
  );
};

export default ResetPassword;
